import { deriveLaborState } from './laborState';
import { LaborCharts } from './LaborCharts';
import type { PendingBirthEntry } from './pendingBirthEntries';
import { LaborTimedTools } from './LaborTimedTools';
import { LaborGrid } from './LaborGrid';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ClinicalEventEntry } from './ClinicalEventEntry';
import { laborGroups, laborFullUpdate } from './laborFields';
import { activeEvents } from './projection';
import type { ClinicalEpisode, ClinicalEvent } from './types';
import type { ProposedEntry } from './PrenatalEntry';
import type { PracticeConfiguration } from './rules';
export interface BirthWorkspaceProps {
  lookupMedicationLots?: (drug: string) => Promise<import('./MedicationLotSuggestions').MedicationLot[]>;
  edd?: string | null;
  timerActions?: import('./BirthMonitoring').BirthTimerAction[];
  onTimerAction?: (action: Omit<import('./BirthMonitoring').BirthTimerAction, 'author_id'>) => Promise<void>;
  pendingEntries?: PendingBirthEntry[];
  episode: ClinicalEpisode;
  events: ClinicalEvent[];
  encounterId: string;
  timeZone: string;
  configuration?: PracticeConfiguration;
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  onEdit?: (event: ClinicalEvent) => void;
}
export function LaborFlowsheet({
  episode,
  events,
  encounterId,
  timeZone,
  disabled,
  onSave,
  onEdit,
  pendingEntries = [],
}: BirthWorkspaceProps) {
  const [group, setGroup] = useState<string>();
  const [chainSubjectId, setChainSubjectId] = useState<string>();
  const [dirty, setDirty] = useState(false);
  const [closeRequested, setCloseRequested] = useState(false);
  const active = activeEvents(events).filter((e) => e.encounter_id === encounterId);
  const nextGroup = group
    ? ({ Birth: 'Apgar', Apgar: 'Cord', Cord: 'First feeding', 'First feeding': 'Placenta' } as Record<string, string>)[
        group
      ]
    : undefined;
  const state = deriveLaborState(events, encounterId);
  async function save(entries: ProposedEntry[], occurredAt: string) {
    for (const entry of entries) {
      if (
        entry.event_type === 'BIRTH' &&
        [...activeEvents(events), ...pendingEntries].some(
          (e) => e.event_type === 'BIRTH' && e.subject_id === entry.subject_id,
        )
      )
        throw new Error(
          'A birth is already documented for this fetus. Review and amend that birth instead of creating another.',
        );
      if (
        entry.event_type === 'MILESTONE' &&
        !entry.payload.repeatable &&
        active.some(
          (e) =>
            e.event_type === 'MILESTONE' &&
            e.subject_id === entry.subject_id &&
            e.payload.milestone === entry.payload.milestone,
        )
      )
        throw new Error(
          'This milestone is already documented. Review the existing entry or explicitly mark a repeatable milestone.',
        );
    }
    await onSave(entries, occurredAt);
    const birth = entries.find((e) => e.event_type === 'BIRTH');
    if (birth)
      setChainSubjectId(
        episode.subjects.find((s) => s.subject_type === 'NEWBORN' && s.fetus_subject_id === birth.subject_id)?.id,
      );
    else {
      const newborn = entries.find((e) =>
        episode.subjects.some((s) => s.id === e.subject_id && s.subject_type === 'NEWBORN'),
      );
      if (newborn) setChainSubjectId(newborn.subject_id);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Labor and birth</CardTitle>
        <CardDescription>
          Event-derived stage: {state.derived}. {state.stated ? `Explicit status: ${String(state.stated)}.` : ''}{' '}
          {state.conflict ? 'The explicit status differs from event chronology; review the source observations.' : ''}{' '}
          New entries require review and are saved to the encrypted queue.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {['Full update', ...Object.keys(laborGroups)].map((key) => (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              key={key}
              disabled={disabled || !encounterId}
              onClick={() => {
                setChainSubjectId(undefined);
                setGroup(key);
                setDirty(false);
                setCloseRequested(false);
              }}>
              {key}
            </Button>
          ))}
        </div>
        <LaborCharts
          events={events}
          subjects={episode.subjects}
          episodeId={episode.id}
          encounterId={encounterId}
          timeZone={timeZone}
          onEdit={onEdit}
        />
        <LaborGrid
          events={events}
          pendingEntries={pendingEntries}
          subjects={episode.subjects}
          encounterId={encounterId}
          timeZone={timeZone}
          onEdit={onEdit}
        />
        <Sheet
          open={!!group}
          onOpenChange={(open) => {
            if (!open) {
              if (dirty) setCloseRequested(true);
              else setGroup(undefined);
            }
          }}>
          <SheetContent
            data-encounter-draft
            onChangeCapture={() => setDirty(true)}
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest('[role="radio"],[role="checkbox"],[data-timed-observation]'))
                setDirty(true);
            }}
            side="bottom"
            className="max-h-[90dvh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{group}</SheetTitle>
              <SheetDescription>
                Enter observed findings. Blank sections are omitted. Times refer to when care occurred.
              </SheetDescription>
            </SheetHeader>
            {closeRequested && (
              <div className="flex flex-wrap gap-2 p-4">
                <p>Discard unsaved entries in this panel?</p>
                <Button type="button" className="min-h-11" onClick={() => setCloseRequested(false)}>
                  Keep editing
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  onClick={() => {
                    setGroup(undefined);
                    setDirty(false);
                  }}>
                  Discard unsaved entries
                </Button>
              </div>
            )}
            {group && (
              <div className="flex flex-col gap-6 p-4">
                {(group === 'Fetal heart rate' || group === 'Contractions') && (
                  <div data-timed-observation>
                    <LaborTimedTools
                      mode={group === 'Fetal heart rate' ? 'FHR' : 'CONTRACTIONS'}
                      subjects={episode.subjects}
                      onSave={save}
                      disabled={disabled}
                    />
                  </div>
                )}
                <ClinicalEventEntry
                  key={group}
                  descriptors={group === 'Full update' ? laborFullUpdate : laborGroups[group]}
                  subjects={episode.subjects}
                  initialSubjectId={chainSubjectId}
                  events={events}
                  disabled={disabled}
                  onSave={save}
                  saveLabel={nextGroup ? `Save and continue to ${nextGroup}` : 'Save observations'}
                  onSaved={() => {
                    setDirty(false);
                    setGroup(nextGroup);
                  }}
                  timeZone={timeZone}
                />
              </div>
            )}
          </SheetContent>
        </Sheet>
      </CardContent>
    </Card>
  );
}
